<script setup lang="ts" name="LoginScreen">
  import { UIAlert, UIButton, UIForm, UIFormItem, UIInput, UITextMdNormal, useNotification } from '@gohighlevel/ghl-ui'
  import { onBeforeUnmount, onMounted, ref } from 'vue'
  import OTPScreen from '@/components/OTPScreen.vue'
  import { AuthService } from '@/service/AuthService'
  import { useStore } from '@/utils/store'
  import { getErrorMessage } from '@/utils/utils'

  import TurnstileWidget from '@/components/TurnstileWidget.vue'
  import { AltType, TURNSTILE_ACTION, TURNSTILE_TYPE, type TurnstileType } from '@/utils/const'
  import { AxiosError } from 'axios'
  import { useI18n } from 'vue-i18n'

  const { t } = useI18n()
  const otpSent = ref(false)
  const store = useStore()
  const formRef = ref()
  const formValue = ref({
    email: '',
  })
  const contactId = ref('')

  const submitLoading = ref(false)

  const notification = useNotification()

  const turnstileWidgetRef = ref<{ reset: () => void } | null>(null)
  const turnstileToken = ref<string | undefined>(undefined)
  const turnstileType = ref<TurnstileType>(TURNSTILE_TYPE.INVISIBLE)
  const turnstileError = ref('')
  // Gates the Send OTP button while the widget is minting a token. Prevents the
  // race where a fast typer clicks Submit before `onTurnstileVerify` has fired
  // and the request goes out with `turnstileToken: undefined` → BE 429
  // CAPTCHA_REQUIRED. Matches the pattern in OneStepOrderFormStepOne / Upsell /
  // Checkout / CreateReviewModal in builder-preview.
  const isCaptchaExecuting = ref(true)
  const CAPTCHA_FALLBACK_TIMEOUT_MS = 10_000
  const captchaFallbackTimeoutRef = ref<number | null>(null)

  const emit = defineEmits(['onLogin'])
  const rules = {
    email: {
      required: true,
      trigger: ['input', 'blur'],
      validator: (_: unknown, value: string) => {
        if (!value) {
          return new Error(t('login.emailRequired'))
        }
        if (!/\S+@\S+\.\S+/.test(value)) {
          return new Error(t('login.emailInvalid'))
        }
        return true
      },
    },
  }

  // Arms (or re-arms) the safety-net timer that unblocks Submit if neither
  // `verify` nor `error` fires within the timeout. Must be called every time
  // `isCaptchaExecuting` is set back to `true`, not just on initial mount —
  // otherwise a degraded widget (Cloudflare api.js blocked / ad-blocker) leaves
  // the button gated forever on the 2nd submit: `finally` re-sets the flag to
  // `true`, `reset()` is a no-op because `window.turnstile` is undefined, and
  // the one-shot mount-time timer has already fired, so nothing clears the flag.
  const armFallback = () => {
    if (captchaFallbackTimeoutRef.value != null) {
      clearTimeout(captchaFallbackTimeoutRef.value)
    }
    captchaFallbackTimeoutRef.value = window.setTimeout(() => {
      if (isCaptchaExecuting.value) {
        isCaptchaExecuting.value = false
      }
      captchaFallbackTimeoutRef.value = null
    }, CAPTCHA_FALLBACK_TIMEOUT_MS)
  }

  const onTurnstileVerify = ({ token }: { token: string }) => {
    if (captchaFallbackTimeoutRef.value != null) {
      clearTimeout(captchaFallbackTimeoutRef.value)
      captchaFallbackTimeoutRef.value = null
    }
    turnstileError.value = ''
    turnstileToken.value = token
    isCaptchaExecuting.value = false
  }

  const onTurnstileExpired = () => {
    turnstileToken.value = undefined
    // Token expired → widget will re-mint; block Submit until the fresh mint lands.
    isCaptchaExecuting.value = true
    armFallback()
  }

  const switchToVisibleChallenge = () => {
    turnstileType.value = TURNSTILE_TYPE.NORMAL
    turnstileToken.value = undefined
    turnstileError.value = t('login.captchaPrompt')
    // User must solve the visible challenge; block Submit until they do.
    isCaptchaExecuting.value = true
    // TurnstileWidget watches `mode` and re-renders itself on change — no
    // imperative call needed here.
  }

  const onTurnstileError = () => {
    // Covers both failure paths the widget surfaces:
    //   1. Cloudflare api.js fails to load / times out (ad-blockers,
    //      locked-down networks) — the widget never renders.
    //   2. Cloudflare runtime error-callback fires after render (expired
    //      token, invalid sitekey, network blip mid-challenge) — the widget
    //      goes dead.
    // Either way, clear any stale token and show a single notification so
    // the user isn't stuck staring at a silent challenge that will never
    // complete with submit still enabled.
    turnstileToken.value = undefined
    // Widget is dead (adblocker / network / Cloudflare error). The BE will 429 the
    // request, but keeping the button gated forever leaves the user stuck staring at
    // a disabled screen. Unblock so they can at least attempt → see toast → retry.
    isCaptchaExecuting.value = false
    notification.create({
      title: t('login.errorTitle'),
      description: t('login.captchaUnavailable'),
      type: 'warning',
      duration: 5000,
    })
  }

  const handleSubmit = async () => {
    submitLoading.value = true

    try {
      await formRef.value?.getForm().validate()
      const response = await AuthService.generateOtp({
        altId: store.locationId.value,
        email: formValue.value.email,
        altType: AltType.LOCATION,
        storeId: store.storeId.value,
        turnstileToken: turnstileToken.value,
        turnstileType: turnstileType.value,
      })

      if (!response.data.status) {
        throw new Error(response.data.message)
      }
      contactId.value = response.data?.data?.contactId
      otpSent.value = true
      turnstileError.value = ''
      // Reset the widget mode back to invisible on success so a subsequent
      // login attempt on the same component instance (e.g. after "Back to
      // login" from the OTP screen) doesn't stay stuck in the visible-mode
      // state that a previous `switchToVisibleChallenge()` set.
      turnstileType.value = TURNSTILE_TYPE.INVISIBLE
    } catch (error: unknown) {
      console.error(error)
      if (error instanceof AxiosError) {
        const status = error.response?.status
        const err = error.response?.data
        // Only escalate to the visible captcha when the rejection is actually
        // captcha-related. A bare HTTP 401 from upstream (e.g., contact lookup
        // failed) should surface as a normal error toast — solving a captcha
        // wouldn't fix it and silently flipping the widget confuses the user.
        const isCaptchaRejection =
          status === 429 ||
          err?.code === 'CAPTCHA_REQUIRED' ||
          err?.code === 'TURNSTILE_VERIFICATION_FAILED' ||
          err?.message === 'CAPTCHA_REQUIRED' ||
          err?.message === 'TURNSTILE_VERIFICATION_FAILED'
        if (isCaptchaRejection) {
          switchToVisibleChallenge()
        } else {
          notification.create({
            title: t('login.errorTitle'),
            description: getErrorMessage(error, t('login.otpError')),
            type: 'error',
            duration: 5000,
          })
        }
      } else {
        notification.create({
          title: t('login.errorTitle'),
          description: getErrorMessage(error, t('login.otpError')),
          type: 'error',
          duration: 5000,
        })
      }
    } finally {
      submitLoading.value = false
      // Cloudflare Turnstile tokens are single-use; clear the stored token
      // and reset the widget after every submit so the next attempt isn't
      // replaying a stale/consumed token.
      turnstileToken.value = undefined
      // Widget is re-minting → gate Submit until the fresh onTurnstileVerify
      // fires. Prevents the same race on a back-to-back OTP request
      // (e.g. logout → login-again on the same page instance).
      isCaptchaExecuting.value = true
      turnstileWidgetRef.value?.reset()
      // Re-arm the safety-net timer so a degraded widget (no `verify` /
      // `error` after reset) doesn't leave Submit disabled forever.
      armFallback()
    }
  }

  // Handles the "Back to login" click on the OTP screen. Resets Turnstile
  // state so the next login attempt starts fresh in invisible mode — otherwise
  // a prior `switchToVisibleChallenge()` (from a captcha rejection earlier in
  // this session) would keep showing the visible captcha after returning to
  // the form.
  //
  // Also re-gates Submit and re-arms the fallback timer: if the user sat on
  // OTPScreen long enough for the previous 10s fallback to have fired,
  // `isCaptchaExecuting` is `false` while the form is hidden. Returning to
  // login without re-blocking Submit lets a quick click fire `generateOtp`
  // with `turnstileToken: undefined` before the remounted widget emits
  // `verify` — the same undefined-token race the fallback pattern was
  // designed to prevent.

  onMounted(() => {
    // Fallback: if the widget never fires `verify` or `error` (e.g. Cloudflare
    // api.js load hangs but neither callback fires), unblock the button after a
    // timeout so the user is not permanently stuck. Same 10s guard used in the
    // builder-preview Turnstile components.
    armFallback()
  })

  onBeforeUnmount(() => {
    if (captchaFallbackTimeoutRef.value != null) {
      clearTimeout(captchaFallbackTimeoutRef.value)
      captchaFallbackTimeoutRef.value = null
    }
  })

  // Handles the "Back to login" click on the OTP screen. Resets Turnstile
  // state so the next login attempt starts fresh in invisible mode — otherwise
  // a prior `switchToVisibleChallenge()` (from a captcha rejection earlier in
  // this session) would keep showing the visible captcha after returning to
  // the form.
  //
  // Also re-gates Submit and re-arms the fallback timer: if the user sat on
  // OTPScreen long enough for the previous 10s fallback to have fired,
  // `isCaptchaExecuting` is `false` while the form is hidden. Returning to
  // login without re-blocking Submit lets a quick click fire `generateOtp`
  // with `turnstileToken: undefined` before the remounted widget emits
  // `verify` — the same undefined-token race the fallback pattern was
  // designed to prevent.
  const handleBackToLogin = () => {
    otpSent.value = false
    turnstileType.value = TURNSTILE_TYPE.INVISIBLE
    turnstileError.value = ''
    turnstileToken.value = undefined
    isCaptchaExecuting.value = true
    armFallback()
    turnstileWidgetRef.value?.reset()
  }

  onMounted(() => {
    // Fallback: if the widget never fires `verify` or `error` (e.g. Cloudflare
    // api.js load hangs but neither callback fires), unblock the button after a
    // timeout so the user is not permanently stuck. Same 10s guard used in the
    // builder-preview Turnstile components.
    armFallback()
  })

  onBeforeUnmount(() => {
    if (captchaFallbackTimeoutRef.value != null) {
      clearTimeout(captchaFallbackTimeoutRef.value)
      captchaFallbackTimeoutRef.value = null
    }
  })
</script>

<template>
  <div v-if="!otpSent" class="max-w-96 mx-auto flex flex-col items-center">
    <h1 class="text-2xl font-semibold text-center">{{ store.storeName }}</h1>
    <UITextMdNormal class="mt-2 text-gray-500">{{ t('login.welcomeBack') }}</UITextMdNormal>
    <UIForm :model="formValue" ref="formRef" id="login-form" :rules="rules" class="w-full">
      <UIFormItem id="email-item" label="" path="email">
        <UIInput v-model="formValue.email" id="email" type="email" :placeholder="t('login.emailPlaceholder')" />
      </UIFormItem>
      <TurnstileWidget
        v-if="store.locationId.value"
        ref="turnstileWidgetRef"
        :mode="turnstileType"
        :action="TURNSTILE_ACTION.ECOMM_CAC_LOGIN"
        @verify="onTurnstileVerify"
        @expired="onTurnstileExpired"
        @error="onTurnstileError"
      />
      <div class="mt-2">
        <UIAlert v-if="turnstileError" id="alert" type="error" :closable="false">
          <template #content>{{ turnstileError }}</template>
        </UIAlert>
      </div>
      <UIButton
        :loading="submitLoading"
        :disabled="isCaptchaExecuting"
        @click="handleSubmit"
        class="w-full mt-4"
        id="login-submit-button"
        type="primary"
      >
        {{ t('login.button') }}
      </UIButton>
    </UIForm>
  </div>
  <OTPScreen v-else :contactId="contactId" :email="formValue.email" @back-to-login="handleBackToLogin" @on-login="emit('onLogin')" />
</template>
